import type { BeforeSendFn } from "posthog-js";

import { geoEventProperties } from "@/lib/vercel-geo";
import { vercelGeoSchema } from "@/schemas";
import type { VercelGeo } from "@/schemas";

/**
 * Adds server-derived geo to cookieless events only. Consented events keep
 * their IP and are enriched by PostHog's server-side GeoIP transformation,
 * which must not be overridden. The geo value is read through a getter so it
 * only ever lives in memory — never in cookies or browser storage.
 */
export function createGeoBeforeSend(
  getGeo: () => VercelGeo | null
): BeforeSendFn {
  return (event) => {
    if (!event) {
      return event;
    }
    const geo = getGeo();
    if (!geo || event.properties?.["$cookieless_mode"] !== true) {
      return event;
    }
    event.properties = {
      ...event.properties,
      ...geoEventProperties(geo),
    };
    return event;
  };
}

/**
 * Fetches the requester's geo from /api/geo. Runs in parallel with PostHog
 * init (never blocking it), so the timeout only bounds how long a hung
 * lookup can keep the promise pending — any failure resolves to null, and
 * events captured before it resolves simply lack geo.
 */
export async function fetchClientGeo(
  timeoutMs = 3000
): Promise<VercelGeo | null> {
  try {
    const response = await fetch("/api/geo", {
      signal: AbortSignal.timeout(timeoutMs),
      cache: "no-store",
    });
    if (!response.ok) {
      return null;
    }
    const data: unknown = await response.json();
    return vercelGeoSchema.nullable().parse(data);
  } catch {
    return null;
  }
}
